import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useConfirmDialog, useI18n } from "@/context";

import * as plansApi from "./plans.api";
import { IUseClientPlans } from "./use-client-plans.hooks.interface";

// Tipo para a resposta da API de planos
export type TPlanResponse = {
  id: string;
  name: string;
  description: string;
  plan_price: number;
  organization_id: string;
  is_active: boolean;
  commission_lifetime_other_installments: number;
  created_at: string;
  updated_at: string;
  plan_features?: string[];
  commission_distribution?: Array<{
    id?: string;
    position: number;
    agency: number;
    commission: number;
    pro_labore?: number;
  }>;
  products: Array<{
    id: string;
    name: string;
    type: string;
    capital_value: number;
    premium_value: number;
    description: string;
    insurance_coverages: string;
    insurance_benefits: string;
    general_conditions: string;
    minimum_price?: number | null;
    plan_name?: string | null;
  }>;
};

export const useClientPlans = (clientId: string): IUseClientPlans => {
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { openDialog } = useConfirmDialog();
  
  const [clientPlans, setClientPlans] = React.useState<TPlanResponse[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = React.useState<TPlanResponse | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isProductsModalOpen, setIsProductsModalOpen] = React.useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = React.useState(false);

  React.useEffect(() => {
    const fetchPlans = async () => {
      if (!clientId) return;

      setIsLoadingPlans(true);
      setError(null);

      try {
        const plans = await plansApi.getPlans(clientId);
        setClientPlans(plans as unknown as TPlanResponse[]);
      } catch (error) {
        console.error("Erro ao buscar planos:", error);
        setError("Não foi possível carregar os planos do cliente.");
        setClientPlans([]);
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [clientId]);

  const getPlanById = async (planId: string) => {
    if (!clientId) return;

    setIsLoadingPlan(true);
    setError(null);

    try {
      const plan = await plansApi.getPlanById(clientId, planId);
      setSelectedPlan(plan as unknown as TPlanResponse);
    } catch (error) {
      console.error("Erro ao buscar plano:", error);
      setError("Não foi possível carregar os dados do plano.");
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const openCommissionModal = async (planId: string) => {
    await getPlanById(planId);
    setIsModalOpen(true);
  };

  const closeCommissionModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const openProductsModal = async (planId: string) => {
    await getPlanById(planId);
    setIsProductsModalOpen(true);
  };

  const closeProductsModal = () => {
    setIsProductsModalOpen(false);
    setSelectedPlan(null);
  };

  const deletePlanMutation = useMutation({
    mutationFn: (planId: string) => plansApi.deletePlan(clientId, planId),
    onSuccess: () => {
      setClientPlans(prev => prev.filter(plan => plan.id !== deletePlanMutation.variables));
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["plans"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.planDeleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message || t("generic.error"),
        variant: "destructive",
      });
    },
  });

  const handleDeletePlan = React.useCallback(
    (planId: string) => {
      deletePlanMutation.mutate(planId);
    },
    [deletePlanMutation]
  );

  const handleBackToClients = React.useCallback(() => {
    navigate("/clients");
  }, [navigate]);

  const handleEditPlan = React.useCallback(
    (planId: string) => {
      if (!clientId) return;
      navigate("/clients", {
        state: {
          fromPlans: true,
          clientId,
          planIds: [planId],
        },
      });
    },
    [clientId, navigate]
  );

  const handleDeletePlanWithConfirmation = React.useCallback(
    (planId: string, planName?: string) => {
      if (!clientId) return;
      openDialog(
        t("generic.confirmDelete"),
        planName || "",
        () => {
          handleDeletePlan(planId);
        }
      );
    },
    [clientId, openDialog, t, handleDeletePlan]
  );

  return {
    clientPlans,
    isLoadingPlans,
    error,
    selectedPlan,
    isModalOpen,
    isProductsModalOpen,
    isLoadingPlan,
    openCommissionModal,
    closeCommissionModal,
    openProductsModal,
    closeProductsModal,
    handleDeletePlan,
    isDeletingPlan: deletePlanMutation.isPending,
    handleBackToClients,
    handleEditPlan,
    handleDeletePlanWithConfirmation,
  };
};
